import { useState } from 'react';

/**
 * Custom hook to manage trip places (add, remove, toggle, move)
 * 
 * Provides functions to manipulate the list of places in a trip,
 * including adding/removing places and reordering them.
 * 
 * @returns Place management functions and state
 */
export function usePlaceManagement() {
  const [tripPlaces, setTripPlaces] = useState<any[]>([]);

  /**
   * Check if a place is already in the trip
   */
  const isPlaceInTrip = (placeId: string): boolean => {
    return tripPlaces.some(p => p.id === placeId);
  };

  /**
   * Add a place to the trip with optional primary location index
   * @param place - The place to add
   * @param primaryLocationIndex - Which primary location this place belongs to (default: 0)
   */
  const addToTrip = (place: any, primaryLocationIndex: number = 0) => {
    if (!isPlaceInTrip(place.id)) {
      // Add primaryLocationIndex to the place object
      const placeWithIndex = { ...place, primaryLocationIndex };
      setTripPlaces(prev => [...prev, placeWithIndex]);
    }
  };

  /**
   * Remove a place from the trip
   */
  const removeFromTrip = (placeId: string) => {
    setTripPlaces(prev => prev.filter(p => p.id !== placeId));
  };

  /**
   * Toggle a place (add if not in trip, remove if already in trip)
   * @param place - The place to toggle
   * @param primaryLocationIndex - Which primary location this place belongs to (default: 0)
   */
  const toggleTripPlace = (place: any, primaryLocationIndex: number = 0) => {
    if (isPlaceInTrip(place.id)) {
      removeFromTrip(place.id);
    } else {
      addToTrip(place, primaryLocationIndex);
    }
  };

  /**
   * Move a place up/down in the trip order
   */
  const movePlace = (index: number, direction: 'up' | 'down' | 'left' | 'right') => {
    const newTripPlaces = [...tripPlaces];
    let newIndex: number;
    
    if (direction === 'up' || direction === 'left') {
      if (index === 0) return; // Already at the start
      newIndex = index - 1;
    } else {
      if (index === tripPlaces.length - 1) return; // Already at the end
      newIndex = index + 1;
    }
    
    // Swap places
    [newTripPlaces[index], newTripPlaces[newIndex]] = [newTripPlaces[newIndex], newTripPlaces[index]];
    setTripPlaces(newTripPlaces);
  };

  /**
   * Helper function to categorize places by type
   */
  const categorizePlaces = (places: any[]) => {
    const counts = {
      attraction: 0,
      experience: 0,
      hotel: 0,
      food: 0
    };
    places.forEach(place => {
      if (place.type && counts.hasOwnProperty(place.type)) {
        counts[place.type as keyof typeof counts]++;
      }
    });
    return counts;
  };

  return {
    tripPlaces,
    setTripPlaces,
    isPlaceInTrip,
    addToTrip,
    removeFromTrip,
    toggleTripPlace,
    movePlace,
    categorizePlaces,
  };
}

